import { useQueryParams } from '@ytl/common-web';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthError, isResponseSuccess, OpenAmAuthError, StageFailure } from '../shared/@types/OpenAmInitialData';
import { InitParams, LoginView, OpenAmStage } from '../shared/@types/OpenAmStage';
import { authenticationResource } from '../shared/resource/authenticationResource';
import { LoginUtils } from '../shared/utils/loginUtils';

export type AuthParams = {
  goto: string;
};

export type OpenAmContextProps =
  | undefined
  | {
      stage: OpenAmStage;
      setStage: (view: LoginView, infoText: string[], name: string, failure?: StageFailure) => void;
      previousStage?: OpenAmStage;
      loadInitialData: () => Promise<void>;
      initParams?: InitParams;
      authIdRenewEnabled: boolean;
      setAuthIdRenewEnabled: (authIdRenewEnabled: boolean) => void;
      setAccountLocked: () => void;
      error?: OpenAmAuthError | AuthError;
      setError: (type?: OpenAmAuthError | AuthError) => void;
      isLoading: boolean;
      setIsLoading: (isLoading: boolean) => void;
      authParams?: AuthParams;
      setInvalidType: () => void;
    };

export const OpenAmContext = createContext<OpenAmContextProps>(undefined);

export const OpenAmProvider = ({ children }: { children: React.ReactNode }) => {
  const [stage, setStage] = useState<OpenAmStage>({
    view: LoginView.Loader,
    infoText: [],
  });
  const [previousStage, setPreviousStage] = useState<OpenAmStage>();
  const [initParams, setInitParams] = useState<InitParams>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<OpenAmAuthError | AuthError | undefined>();
  const [authIdRenewEnabled, setAuthIdRenewEnabled] = useState(false);
  const [oauthParams, setOauthParams] = useState<AuthParams>();
  const query = useQueryParams();

  const updateStage = useCallback(
    (nextStage: OpenAmStage) => {
      setPreviousStage(stage);
      setStage(nextStage);
    },
    [stage, setPreviousStage, setStage],
  );

  const loadInitialData = useCallback(async () => {
    if (new URLSearchParams(window.location.search).has('_rs')) {
      console.log('realm switch detected');
      updateStage({
        view: LoginView.LoginForm,
      });
      setError(OpenAmAuthError.RealmSwitch);
      return;
    }
    if (window.location.pathname.endsWith('/login-actions/authenticate')) {
      // @ts-ignore
      if (window.yuiContext.deviceLoginSuccess === 'true' || window.yuiContext.deviceLoginSuccess === true) {
        setStage({
          view: LoginView.DeviceCodeSuccess,
          infoText: [],
        });
        return;
      }
    }
    if (window.location.pathname.endsWith('/device')) {
      // @ts-ignore
      if (window.yuiContext.deviceLoginSuccess === 'false' || window.yuiContext.deviceLoginSuccess === false) {
        setStage({
          view: LoginView.DeviceCode,
          infoText: [],
        });
        return;
      }
    }

    if (window.location.pathname.endsWith('/device/user')) {
      if (new URLSearchParams(window.location.search).has('user_code')) {
        setStage({
          view: LoginView.DeviceCodeSuccess,
          infoText: [],
        });
      } else {
        setStage({
          view: LoginView.DeviceCode,
          infoText: [],
        });
      }
      return;
    }
    try {
      const goto = new URLSearchParams(window.location.search).get('goto') || '';
      setOauthParams({
        goto,
      });
      try {
        const sessionCheckResult = await authenticationResource.checkSession();
        if (sessionCheckResult.status === 200) {
          LoginUtils.navigateSuccess(
            goto,
            sessionCheckResult.data?.successURL,
            sessionCheckResult.data?.realm,
            () => {},
          );
          return;
        }
      } catch (e) {
        console.log('session check error', e);
      }

      const initialData = await authenticationResource.getInitialData();

      if (isResponseSuccess(initialData.data)) {
        LoginUtils.navigateSuccess(goto, initialData.data.successUrl, initialData.data.realm, () => {});
        return;
      }

      if (initialData.data?.authId) {
        const isForcedSMSLogin = query.get('view') === 'sms-login';
        const view = isForcedSMSLogin
          ? LoginView.SMSPhoneNumber
          : LoginUtils.getLoginViewByStage(initialData.data?.stage);
        setAuthIdRenewEnabled(true);

        updateStage({
          view,
          authId: initialData.data?.authId,
          name: initialData.data?.stage,
          infoText: initialData.data?.infoText || [],
        });
        try {
          let parsed: InitParams = JSON.parse(initialData.data?.infoText?.[0] ?? '');
          if (!parsed.realm) {
            parsed.realm = 'b2c';
          }
          setInitParams(parsed);
        } catch (err) {}
      } else {
        updateStage({
          view: LoginView.LoginForm,
          error: 'initial data error',
        });
      }
    } catch (err) {
      console.error(`Initial data loading error`, err);
      updateStage({
        view: LoginView.LoginForm,
      });
      setError(OpenAmAuthError.InitError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount, and when query changes
  }, [query]);

  const value: OpenAmContextProps = useMemo(
    () => ({
      stage,
      setStage: (view: LoginView, infoText: string[], name: string, failure?: StageFailure) => {
        setStage(prevState => {
          setPreviousStage(prevState);

          return {
            ...prevState,
            name,
            view,
            infoText,
            failure,
          };
        });
      },
      initParams,
      authIdRenewEnabled,
      setAuthIdRenewEnabled,
      loadInitialData,
      setAccountLocked: () => setError(OpenAmAuthError.AccountIsLocked),
      isLoading,
      setIsLoading,
      error,
      authParams: oauthParams,
      setError,
      previousStage,
      setInvalidType: () => setError(OpenAmAuthError.InvalidTypeB2B),
    }),
    [
      stage,
      authIdRenewEnabled,
      isLoading,
      oauthParams,
      error,
      setError,
      initParams,
      loadInitialData,
      previousStage,
      setPreviousStage,
    ],
  );

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return <OpenAmContext.Provider value={value}>{children}</OpenAmContext.Provider>;
};

export const useOpenAm = () => {
  const context = useContext(OpenAmContext);
  if (context === undefined) {
    throw new Error(`useOpenAm must be used within an OpenAmContext`);
  }
  return context;
};
